import React, { ReactElement, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import cn from "classnames";
import { MenuInfo } from "rc-menu/lib/interface";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import { Company, Deal, EntitySupportingCard, Person } from "@mapmycustomers/shared/types/entity";
import { stopEventPropagation } from "@mapmycustomers/shared/util/browser";
import { useModal } from "@mapmycustomers/ui";

import ActionsBar from "@app/component/ActionsBar";
import {
  createActivity,
  showGlobalCreateActivityModal,
} from "@app/component/createEditEntity/Activity/store/actions";
import { getActivityTypes } from "@app/store/activity";
import { getFunnelStages } from "@app/store/deal";
import { updateDeal } from "@app/store/deal/actions";
import { initFrequencyModal } from "@app/store/frequency/actions";
import { getCurrentUser } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useClickTrackerContext from "@app/util/contexts/useClickTrackerContext";
import { getEntityTypeAnalyticsName } from "@app/util/ui";

import Actions from "./Actions";
import ModalForActions from "./ModalForActions";
import styles from "./RecordActionsBar.module.scss";
import useCommonActions from "./useCommonActions";
import useCompanyPersonActions from "./useCompanyPersonActions";
import useDealActions from "./useDealActions";

type RecordActionsBarProps = {
  className?: string;
  companies?: Company[];
  enableGeneric?: boolean;
  entity: EntitySupportingCard;
  extraActions?: ReactElement<{ entity?: EntitySupportingCard }>;
  onAddNoteVisibilityChange?: (visibility: boolean) => void;
  onCreateActivity: typeof createActivity.request;
  onEdit?: () => void;
  onEmailSubscribe?: () => void;
  onEmailUnsubscribe?: () => void;
  onFetchNotes?: () => void;
  people?: Person[];
} & ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps;

const RecordActionsBar = ({
  activityTypes,
  className,
  companies,
  currentUser,
  enableGeneric = true,
  entity,
  extraActions,
  funnelStages,
  onAddNoteVisibilityChange,
  onCreateActivity,
  onEdit,
  onEmailSubscribe,
  onEmailUnsubscribe,
  onFetchNotes,
  onInitFrequencyModal,
  onShowCreateActivityModal,
  onUpdateDeal,
  people,
}: RecordActionsBarProps) => {
  const analytics = useExtendedAnalytics(
    `${getEntityTypeAnalyticsName(entity.entity, false, false)} Pin`
  );
  const clickTrackerContext = useClickTrackerContext();
  const [actionKey, setActionKey] = useState<string | undefined>();
  const [modal, contextHolder] = useModal();
  const [editStageDeal, setEditStageDeal] = useState<Deal | undefined>();
  const isDeal = entity.entity === EntityType.DEAL;
  const handleQuickActionButtonClick = useCallback(
    (info: MenuInfo) => {
      if (
        [
          Actions.ADD_DEAL,
          Actions.MANAGE_CHILD_COMPANIES,
          Actions.MANAGE_COMPANIES,
          Actions.MANAGE_PARENT_COMPANY,
          Actions.MANAGE_PERSON,
        ].includes(info.key as Actions)
      ) {
        clickTrackerContext.click(Actions.MANAGE_RELATIONSHIP);
      } else {
        clickTrackerContext.click(info.key);
      }
      setActionKey(info.key);
      if (info.key === Actions.ADD_NOTE) {
        onAddNoteVisibilityChange?.(true);
      }
    },
    [clickTrackerContext, onAddNoteVisibilityChange, setActionKey]
  );

  const handleModalHide = useCallback(() => {
    setActionKey((prevKey) => {
      if (prevKey === Actions.ADD_NOTE) {
        onAddNoteVisibilityChange?.(false);
      }
      return undefined;
    });
    setEditStageDeal(undefined);
  }, [onAddNoteVisibilityChange]);

  const extraActionsElement = useMemo(() => {
    if (extraActions) {
      return React.cloneElement(extraActions, {
        entity,
      });
    }
    return undefined;
  }, [extraActions, entity]);

  const commonActions = useCommonActions(
    currentUser,
    entity,
    handleQuickActionButtonClick,
    onEdit,
    onInitFrequencyModal,
    onShowCreateActivityModal
  );
  const dealActions = useDealActions(
    currentUser,
    entity as Deal,
    funnelStages,
    handleQuickActionButtonClick,
    onUpdateDeal,
    setEditStageDeal
  );
  const companyPersonActions = useCompanyPersonActions(
    analytics,
    activityTypes,
    currentUser,
    entity,
    handleQuickActionButtonClick,
    modal,
    { onCreateActivity, onShowCreateActivityModal },
    onEmailSubscribe,
    onEmailUnsubscribe
  );

  const quickActions = useMemo(
    () =>
      (isDeal
        ? [...commonActions, ...dealActions]
        : [...commonActions, ...companyPersonActions]
      ).sort(clickTrackerContext.comparator),
    // clickTrackerContext.clicks is required to watch for change in number of clicks on each quick action button
    // eslint-disable-next-line
    [
      clickTrackerContext.comparator,
      clickTrackerContext.clicks,
      commonActions,
      companyPersonActions,
      dealActions,
      isDeal,
    ]
  );

  return (
    <div className={cn(styles.container, className)} onClick={stopEventPropagation}>
      <AnalyticsContext.Provider value={analytics}>
        {extraActionsElement}
        {enableGeneric && (
          <ActionsBar
            actions={quickActions}
            className={styles.actionBar}
            onClick={handleQuickActionButtonClick}
          />
        )}
        <ModalForActions
          actionKey={actionKey}
          assignedCompanies={companies}
          assignedPeople={
            entity.entity === EntityType.DEAL && entity.contact ? [entity.contact] : people
          }
          deal={editStageDeal}
          entity={entity}
          onFetchNotes={onFetchNotes}
          onModalHide={handleModalHide}
          parentCompany={
            entity.entity === EntityType.DEAL
              ? entity.account
              : entity.entity === EntityType.COMPANY
                ? entity.parentAccount
                : undefined
          }
        />
      </AnalyticsContext.Provider>
      {contextHolder}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  activityTypes: getActivityTypes(state),
  currentUser: getCurrentUser(state)!,
  funnelStages: getFunnelStages(state),
});

const mapDispatchToProps = {
  onCreateActivity: createActivity.request,
  onInitFrequencyModal: initFrequencyModal.request,
  onShowCreateActivityModal: showGlobalCreateActivityModal,
  onUpdateDeal: updateDeal.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(RecordActionsBar);
